import { useState, useMemo, useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { PaginationBar } from "@/components/PaginationBar";
import { BackToLeadPerformance } from "@/components/BackToLeadPerformance";
import { usePagination } from "@/hooks/usePagination";
import { getChannelById, resolveChannelFromSource } from "@/lib/growthChannelMapping";
import {
  Search, TrendingUp, Target, Phone, DollarSign, BarChart3, ArrowRight,
  Trophy, Zap, AlertTriangle, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Filter, X,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Job } from "@/data/types";
import { supabase } from "@/lib/supabase";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";
import { format as formatDateFn, parseISO } from "date-fns";

/* ═══════════════════════════════════════════
   MARKETING ATTRIBUTION
   Dedicated attribution & reporting layer.
   Analytical companion to Growth Channels.
   ═══════════════════════════════════════════ */

/* ── Derive won outcome status from job state ── */
function deriveWonOutcome(j: Job): "Net" | "Pending" | "Canceled" | "Finance Declined" | "—" {
  if (j.rescissionStatus === "In Rescission") return "Pending";
  if (j.currentStage === "Completed" || j.rescissionStatus === "Cleared for Production") return "Net";
  if ((j as any).canceledDate || (j.currentStage as string) === "Canceled") return "Canceled";
  if ((j as any).financeDeclined) return "Finance Declined";
  return "—";
}

/* ── Resolve channel name from lead source ── */
function resolveChannelName(leadSource: string): string {
  const channelId = resolveChannelFromSource(leadSource);
  if (!channelId) return "Unattributed";
  const ch = getChannelById(channelId);
  return ch?.name || channelId;
}

/* ── Lead quality tag ── */
function deriveLeadQuality(j: Job, issueCount: number): "Valid" | "Out-of-Area" | "Low Quality" {
  if (issueCount >= 2) return "Low Quality";
  // Out-of-area heuristic: no branch assigned or flagged
  if (!j.branch) return "Out-of-Area";
  return "Valid";
}

/* ── Booking score (lightweight heuristic) ── */
function deriveBookingScore(j: Job): number {
  let score = 70;
  if (j.financials?.contractValue && j.financials.contractValue >= 5000) score += 10;
  if (j.marketing?.leadCost && j.marketing.leadCost < 150) score += 8;
  if (j.rescissionStatus === "Cleared for Production") score += 7;
  if (j.currentStage === "Completed") score += 5;
  return Math.min(score, 100);
}

function formatApptDisplay(value: string): string {
  const v = String(value ?? "").trim();
  if (!v) return "—";
  try {
    return formatDateFn(parseISO(v), "MMM d, h:mm a");
  } catch {
    return v;
  }
}

/* ── Job display reference (jobNumber or UUID fallback) ── */
function getJobDisplayRef(job: Job): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

/* ── Unified decision engine (shared with Growth Channels) ── */
import { deriveDecision, getStatusStyle, getActionStyle, type DecisionInput } from "@/lib/decisionEngine";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

function buildCampaignDecisionInput(jobs: number, netJobs: number, leadCost: number, validLeadPct?: number): DecisionInput {
  return {
    netConversionPct: jobs > 0 ? (netJobs / jobs) * 100 : 0,
    cpa: netJobs > 0 ? leadCost / netJobs : 999,
    leadQualityPct: validLeadPct ?? (jobs > 0 ? Math.min(90, 60 + (netJobs / jobs) * 40) : 0),
    jobs,
  };
}

export type MarketingAttributionSection = "attribution" | "callcenter" | "campaigns";

interface MarketingAttributionProps {
  /** When embedded inside Lead Performance, suppress the legacy inner header,
   *  back link, subtitle, and standalone KPI grid. The parent owns the header
   *  and renders consolidated metrics in a Channels/Sources-style stat strip. */
  embedded?: boolean;
  /** Which slice to render. Defaults to the full report when not embedded; the
   *  Lead Performance hub passes one of these per tab. */
  section?: MarketingAttributionSection;
}

export default function MarketingAttribution({ embedded = false, section }: MarketingAttributionProps = {}) {
  // When not scoped, render every section (legacy/standalone behavior).
  const showAttribution = !section || section === "attribution";
  const showCallCenter  = !section || section === "callcenter";
  const showCampaigns   = !section || section === "campaigns";
  const { jobs, issues, leadSources, salesOpportunities, leads } = useAppData();
  const [search, setSearch] = useState("");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  // Sort state for all tables
  const [sourceSort, setSourceSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "revenue", dir: "desc" });
  const [repSort, setRepSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "revenue", dir: "desc" });
  const [jobSort, setJobSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "value", dir: "desc" });

  // Filter state for job-level table
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [qualityFilter, setQualityFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [dbSourceData, setDbSourceData] = useState<Array<{
    source: string;
    leads: number;
    jobs: number;
    revenue: number;
    issues: number;
    closed: number;
    netJobs: number;
    leadCost: number;
    cpaNet: number;
    netConversion: number;
    leadQuality: number;
  }> | null>(null);
  const [dbJobAttributionByJobId, setDbJobAttributionByJobId] = useState<Map<string, {
    leadSource: string;
    leadCost: number;
    appointmentTime: string;
    leadId: string;
  }> | null>(null);
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const isMissingRep = (value: string | null | undefined): boolean => {
    const normalized = String(value ?? "").trim().toLowerCase();
    return (
      normalized.length === 0 ||
      normalized === "unassigned" ||
      normalized === "unknown" ||
      normalized === "n/a" ||
      normalized === "na" ||
      normalized === "null" ||
      normalized === "undefined" ||
      normalized === "—" ||
      normalized === "-"
    );
  };

  const allJobs = filterByBranchRegion(jobs, brFilter);
  const jobsWithMarketing = allJobs.filter((j) => j.marketing);

  // ── Source-level aggregation (DB truth: leads -> quotes -> jobs) ──
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [leadsRes, quotesRes, jobsRes, issuesRes, apptResultsRes, leadSourcesRes] = await Promise.all([
        supabase.from("leads").select("id, lead_source_id, lead_source, source_name, lead_cost, appointment_time, created_at"),
        supabase.from("quotes").select("lead_id, linked_job_id, appointment_id"),
        supabase.from("jobs").select("id, current_stage, financials"),
        supabase.from("issues").select("id, job_id"),
        supabase.from("appointment_results").select("appointment_id, scheduled_at"),
        supabase.from("lead_sources").select("id, name, cost_per_lead, config"),
      ]);

      if (cancelled) return;
      if (leadsRes.error || quotesRes.error || jobsRes.error || issuesRes.error || apptResultsRes.error || leadSourcesRes.error) {
        console.warn("MarketingAttribution: failed DB source aggregation", {
          leadsError: leadsRes.error,
          quotesError: quotesRes.error,
          jobsError: jobsRes.error,
          issuesError: issuesRes.error,
          apptResultsError: apptResultsRes.error,
          leadSourcesError: leadSourcesRes.error,
        });
        setDbSourceData([]);
        setDbJobAttributionByJobId(new Map());
        return;
      }

      const leadSourceNameById = new Map<string, string>();
      for (const source of leadSources) {
        const id = String(source.id ?? "").trim();
        const name = String(source.name ?? "").trim();
        if (id && name) leadSourceNameById.set(id, name);
      }

      const quotesByLeadId = new Map<string, string[]>();
      const quoteRowsByLeadAndJob = new Map<string, { appointmentId: string }>();
      for (const row of quotesRes.data ?? []) {
        const leadId = String(row.lead_id ?? "").trim();
        const linkedJobId = String(row.linked_job_id ?? "").trim();
        if (!leadId || !linkedJobId) continue;
        const arr = quotesByLeadId.get(leadId) ?? [];
        arr.push(linkedJobId);
        quotesByLeadId.set(leadId, arr);
        quoteRowsByLeadAndJob.set(`${leadId}::${linkedJobId}`, {
          appointmentId: String((row as { appointment_id?: string | null }).appointment_id ?? "").trim(),
        });
      }

      const appointmentTimeById = new Map<string, string>();
      for (const row of apptResultsRes.data ?? []) {
        const appointmentId = String(row.appointment_id ?? "").trim();
        const scheduledAt = String(row.scheduled_at ?? "").trim();
        if (!appointmentId || !scheduledAt) continue;
        const existing = appointmentTimeById.get(appointmentId);
        if (!existing || new Date(scheduledAt).getTime() > new Date(existing).getTime()) {
          appointmentTimeById.set(appointmentId, scheduledAt);
        }
      }

      const jobsById = new Map<string, { currentStage: string; value: number }>();
      const appJobValueById = new Map<string, number>();
      for (const job of jobs) {
        appJobValueById.set(String(job.id), Number(getJobDisplayValue(job) || 0));
      }
      for (const row of jobsRes.data ?? []) {
        const id = String(row.id ?? "").trim();
        if (!id) continue;
        const currentStage = String(row.current_stage ?? "");
        const dbValue = Number((row.financials as { contractValue?: number } | null)?.contractValue ?? 0);
        const value = dbValue > 0 ? dbValue : Number(appJobValueById.get(id) ?? 0);
        jobsById.set(id, { currentStage, value });
      }
      const issueCountByJobId = new Map<string, number>();
      for (const row of issuesRes.data ?? []) {
        const jobId = String(row.job_id ?? "").trim();
        if (!jobId) continue;
        issueCountByJobId.set(jobId, (issueCountByJobId.get(jobId) ?? 0) + 1);
      }

      type SourceBilling = { mode: "per_lead" | "per_day"; costPerLead: number; dailyRate: number };
      const billingBySourceId = new Map<string, SourceBilling>();
      const billingBySourceName = new Map<string, SourceBilling>();
      for (const row of (leadSourcesRes.data ?? []) as Array<Record<string, unknown>>) {
        const id = String(row.id ?? "").trim();
        const name = String(row.name ?? "").trim();
        const costPerLead = Number(row.cost_per_lead ?? 0);
        const cfg = (row.config ?? {}) as Record<string, unknown>;
        const cfgMode = String(
          cfg.spend_model ?? cfg.billing_model ?? cfg.cost_mode ?? cfg.rate_type ?? "",
        ).trim().toLowerCase();
        const mode: "per_lead" | "per_day" =
          cfgMode === "per_day" || cfgMode === "daily" ? "per_day" : "per_lead";
        const dailyRate = Number(cfg.daily_rate ?? cfg.rate_per_day ?? cfg.day_rate ?? 0);
        const billing: SourceBilling = { mode, costPerLead, dailyRate };
        if (id) billingBySourceId.set(id, billing);
        if (name) billingBySourceName.set(name.toLowerCase(), billing);
      }

      type Agg = {
        source: string;
        sourceId: string;
        leads: number;
        jobs: number;
        revenue: number;
        issues: number;
        closed: number;
        netJobs: number;
        leadCost: number;
        leadCostObserved: number;
        activeLeadDays: Set<string>;
      };
      const map = new Map<string, Agg>();
      const jobAttribution = new Map<string, { leadSource: string; leadCost: number; appointmentTime: string; leadId: string }>();
      const ensure = (sourceId: string, sourceName: string) => {
        const source = sourceName.trim() || "Unmapped";
        const key = sourceId || source.toLowerCase();
        const existing = map.get(key);
        if (existing) return existing;
        const created: Agg = {
          source,
          sourceId,
          leads: 0,
          jobs: 0,
          revenue: 0,
          issues: 0,
          closed: 0,
          netJobs: 0,
          leadCost: 0,
          leadCostObserved: 0,
          activeLeadDays: new Set<string>(),
        };
        map.set(key, created);
        return created;
      };

      // Seed configured lead sources so zero-volume sources still show.
      for (const source of leadSources) {
        const name = String(source.name ?? "").trim();
        if (!name) continue;
        ensure(String(source.id ?? "").trim(), name);
      }

      for (const lead of leadsRes.data ?? []) {
        const sourceId = String(lead.lead_source_id ?? "").trim();
        const explicitName = String(lead.lead_source ?? lead.source_name ?? "").trim();
        const sourceName = sourceId ? (leadSourceNameById.get(sourceId) ?? explicitName) : explicitName;
        const agg = ensure(sourceId, sourceName);
        agg.leads += 1;

        const leadCost = Number(lead.lead_cost ?? 0);
        if (leadCost > 0) agg.leadCostObserved += leadCost;
        const createdAt = String((lead as { created_at?: string | null }).created_at ?? "").trim();
        if (createdAt) agg.activeLeadDays.add(createdAt.slice(0, 10));

        const linkedJobs = quotesByLeadId.get(String(lead.id ?? "")) ?? [];
        agg.jobs += linkedJobs.length;
        for (const jobId of linkedJobs) {
          const job = jobsById.get(jobId);
          if (!job) continue;
          const existing = jobAttribution.get(jobId);
          if (!existing || !existing.leadSource) {
            const quoteKey = `${String(lead.id ?? "").trim()}::${jobId}`;
            const appointmentId = quoteRowsByLeadAndJob.get(quoteKey)?.appointmentId ?? "";
            const quoteAppt = appointmentId ? String(appointmentTimeById.get(appointmentId) ?? "").trim() : "";
            const leadAppt = String((lead as { appointment_time?: string | null }).appointment_time ?? "").trim();
            jobAttribution.set(jobId, {
              leadSource: agg.source,
              leadCost,
              appointmentTime: quoteAppt || leadAppt,
              leadId: String(lead.id ?? "").trim(),
            });
          }
          agg.revenue += job.value;
          agg.issues += issueCountByJobId.get(jobId) ?? 0;
          const stageNorm = job.currentStage.trim().toLowerCase();
          if (stageNorm === "completed") {
            agg.closed += 1;
            agg.netJobs += 1;
          }
        }
      }

      const rows = Array.from(map.values()).map((d) => {
        const billing =
          billingBySourceId.get(d.sourceId) ??
          billingBySourceName.get(d.source.toLowerCase()) ??
          { mode: "per_lead" as const, costPerLead: 0, dailyRate: 0 };

        const leadCost =
          billing.mode === "per_day"
            ? (billing.dailyRate > 0 ? billing.dailyRate * d.activeLeadDays.size : d.leadCostObserved)
            : (d.leadCostObserved > 0 ? d.leadCostObserved : (billing.costPerLead > 0 ? billing.costPerLead * d.leads : 0));

        return {
          source: d.source,
          leads: d.leads,
          jobs: d.jobs,
          revenue: d.revenue,
          issues: d.issues,
          closed: d.closed,
          netJobs: d.netJobs,
          leadCost,
          cpaNet: d.netJobs > 0 ? leadCost / d.netJobs : 0,
          netConversion: d.leads > 0 ? (d.netJobs / d.leads) * 100 : 0,
          leadQuality: d.leads > 0 ? Math.max(0, ((d.leads - d.issues) / d.leads) * 100) : 100,
        };
      });

      const k = sourceSort.key as keyof (typeof rows)[0];
      rows.sort((a, b) => {
        const av = a[k] as number | string;
        const bv = b[k] as number | string;
        if (typeof av === "string" && typeof bv === "string") {
          return sourceSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        return sourceSort.dir === "asc"
          ? Number(av) - Number(bv)
          : Number(bv) - Number(av);
      });

      setDbSourceData(rows);
      setDbJobAttributionByJobId(jobAttribution);
    })();

    return () => {
      cancelled = true;
    };
  }, [leadSources, sourceSort, jobs]);

  const sourceData = dbSourceData ?? [];
  const hasDbJobAttribution = !!dbJobAttributionByJobId && dbJobAttributionByJobId.size > 0;
  const leadSourceForJob = (j: Job): string =>
    String(dbJobAttributionByJobId?.get(j.id)?.leadSource ?? "").trim();
  const leadCostForJob = (j: Job): number => {
    const dbCost = Number(dbJobAttributionByJobId?.get(j.id)?.leadCost ?? 0);
    return dbCost > 0 ? dbCost : 0;
  };
  const apptDateForJob = (j: Job): string =>
    String(dbJobAttributionByJobId?.get(j.id)?.appointmentTime ?? "").trim();
  const leadIdForJob = (j: Job): string =>
    String(dbJobAttributionByJobId?.get(j.id)?.leadId ?? "").trim();

  const jobsForAttribution = useMemo(() => {
    if (!hasDbJobAttribution) return [];
    return allJobs.filter((j) => dbJobAttributionByJobId?.has(j.id));
  }, [hasDbJobAttribution, allJobs, jobsWithMarketing, dbJobAttributionByJobId]);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          jobsForAttribution
            .flatMap((j) => {
              const leadId = leadIdForJob(j);
              const opportunityRep =
                salesOpportunities.find(
                  (o) => String(o.leadId ?? "").trim() === leadId || String(o.id ?? "").trim() === leadId,
                )?.assignedRep ?? "";
              const leadRep =
                leads.find((l) => String(l.id ?? "").trim() === leadId)?.assignedRep ?? "";
              return [
                String(leadRep).trim(),
                String(opportunityRep).trim(),
                String(j.marketing?.callCenterRep ?? "").trim(),
                String(j.salesRep ?? "").trim(),
              ];
            })
            .filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }
      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);
      if (cancelled) return;
      const map = new Map<string, string>();
      for (const row of [...(byIdRes.data ?? []), ...(byAuthIdRes.data ?? [])]) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const full = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        if (!full) continue;
        const id = String(row.id ?? "").trim();
        const authId = String(row.auth_user_id ?? "").trim();
        if (id) map.set(id, full);
        if (authId) map.set(authId, full);
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [jobsForAttribution, salesOpportunities, leads, dbJobAttributionByJobId]);

  const repForJob = (j: Job): string => {
    const leadId = leadIdForJob(j);
    const opportunityRep =
      salesOpportunities.find(
        (o) => String(o.leadId ?? "").trim() === leadId || String(o.id ?? "").trim() === leadId,
      )?.assignedRep ?? "";
    const leadRep =
      leads.find((l) => String(l.id ?? "").trim() === leadId)?.assignedRep ?? "";
    const attribution = dbJobAttributionByJobId?.get(String(j.id ?? "").trim());
    const quoteRep = String(attribution?.salesRep ?? attribution?.repName ?? "").trim();
    const raw = String(
      (!isMissingRep(leadRep) ? leadRep : "") ||
      (!isMissingRep(quoteRep) ? quoteRep : "") ||
      (!isMissingRep(opportunityRep) ? opportunityRep : "") ||
      j.marketing?.callCenterRep ||
      j.salesRep ||
      "",
    ).trim();
    return resolveProfileDisplayName(raw, repNameById, "—");
  };

  // ── Campaign aggregation (enhanced) ──
  const campaignData = useMemo(() => {
    const map = new Map<string, { jobs: number; revenue: number; leadCost: number; netJobs: number; sources: Set<string>; channels: Set<string> }>();
    jobsWithMarketing.forEach((j) => {
      const c = j.marketing!.campaignName;
      const entry = map.get(c) || { jobs: 0, revenue: 0, leadCost: 0, netJobs: 0, sources: new Set<string>(), channels: new Set<string>() };
      entry.jobs += 1;
      entry.revenue += getJobDisplayValue(j);
      entry.leadCost += j.marketing?.leadCost || 0;
      if (j.rescissionStatus === "Cleared for Production" || j.currentStage === "Completed") entry.netJobs += 1;
      entry.sources.add(j.marketing!.leadSource);
      entry.channels.add(resolveChannelName(j.marketing!.leadSource));
      map.set(c, entry);
    });
    return Array.from(map.entries())
      .map(([campaign, d]) => {
        const input = buildCampaignDecisionInput(d.jobs, d.netJobs, d.leadCost);
        const decision = deriveDecision(input);
        const netConversion = d.jobs > 0 ? (d.netJobs / d.jobs) * 100 : 0;
        const cpa = d.netJobs > 0 ? d.leadCost / d.netJobs : 0;
        return {
          campaign,
          jobs: d.jobs,
          revenue: d.revenue,
          leadCost: d.leadCost,
          netJobs: d.netJobs,
          netConversion,
          cpa,
          sources: Array.from(d.sources).join(", "),
          channel: Array.from(d.channels).join(", "),
          status: decision.status,
          action: decision.action,
          descriptor: decision.descriptor,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [jobsWithMarketing]);

  // ── Call center rep performance ──
  // Speed-to-lead is derived deterministically per rep from a stable hash so
  // values are consistent across renders in prototype mode (no real timestamps yet).
  const repData = useMemo(() => {
    const map = new Map<string, { appointments: number; closed: number; revenue: number; highFitClosed: number; highFitTotal: number; leadSpend: number; noShows: number }>();
    jobsWithMarketing.forEach((j) => {
      const rep = repForJob(j);
      if (!rep || rep === "—") return;
      const entry = map.get(rep) || { appointments: 0, closed: 0, revenue: 0, highFitClosed: 0, highFitTotal: 0, leadSpend: 0, noShows: 0 };
      entry.appointments += 1;
      entry.leadSpend += leadCostForJob(j);
      const isHighFit = getJobDisplayValue(j) >= 4000;
      if (isHighFit) entry.highFitTotal += 1;
      const outcome = (j.marketing!.saleOutcome || "").toLowerCase();
      if (outcome.includes("no show") || outcome.includes("no-show")) entry.noShows += 1;
      if (j.marketing!.saleOutcome === "Closed") {
        entry.closed += 1;
        entry.revenue += getJobDisplayValue(j);
        if (isHighFit) entry.highFitClosed += 1;
      }
      map.set(rep, entry);
    });
    const rows = Array.from(map.entries())
      .map(([rep, d]) => {
        // Stable hash → speed-to-lead seconds (45s–11min range)
        let h = 0;
        for (let i = 0; i < rep.length; i++) h = (h * 31 + rep.charCodeAt(i)) >>> 0;
        const speedSec = 45 + (h % 600);
        const showRate = d.appointments > 0 ? ((d.appointments - d.noShows) / d.appointments) * 100 : 0;
        return {
          rep,
          ...d,
          closeRate: d.appointments > 0 ? (d.closed / d.appointments) * 100 : 0,
          highFitConversion: d.highFitTotal > 0 ? (d.highFitClosed / d.highFitTotal) * 100 : 0,
          speedSec,
          showRate,
          costPerAppt: d.appointments > 0 ? d.leadSpend / d.appointments : 0,
          revenuePerAppt: d.appointments > 0 ? d.revenue / d.appointments : 0,
        };
      });
    const k = repSort.key as keyof (typeof rows)[0];
    rows.sort((a, b) => {
      const av = a[k] as number, bv = b[k] as number;
      return repSort.dir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [jobsWithMarketing, repSort, repNameById]);

  // ── Issue count per job (for quality signal) ──
  const jobIssueCount = useMemo(() => {
    const map = new Map<string, number>();
    issues.forEach((i) => map.set(i.jobId, (map.get(i.jobId) || 0) + 1));
    return map;
  }, [issues]);

  // ── Unique values for filter dropdowns ──
  const uniqueChannels = useMemo(
    () => [...new Set(jobsForAttribution.map((j) => resolveChannelName(leadSourceForJob(j))))].sort(),
    [jobsForAttribution, dbJobAttributionByJobId],
  );
  const uniqueStatuses = ["Net", "Pending", "Canceled", "Finance Declined", "—"];
  const uniqueQualities = ["Valid", "Out-of-Area", "Low Quality"];

  // ── Filtered job list for attribution table ──
  let filtered = jobsForAttribution;
  if (selectedSource) filtered = filtered.filter((j) => leadSourceForJob(j) === selectedSource);
  if (channelFilter !== "all") filtered = filtered.filter((j) => resolveChannelName(leadSourceForJob(j)) === channelFilter);
  if (statusFilter !== "all") filtered = filtered.filter(j => deriveWonOutcome(j) === statusFilter);
  if (qualityFilter !== "all") filtered = filtered.filter(j => deriveLeadQuality(j, jobIssueCount.get(j.id) || 0) === qualityFilter);
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter((j) =>
      j.id.toLowerCase().includes(s) || j.customerName.toLowerCase().includes(s) ||
      leadSourceForJob(j).toLowerCase().includes(s) || String(j.marketing?.campaignName ?? "").toLowerCase().includes(s)
    );
  }

  // ── Sort filtered jobs ──
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const k = jobSort.key;
    arr.sort((a, b) => {
      let av: number | string = 0, bv: number | string = 0;
      if (k === "value") { av = getJobDisplayValue(a); bv = getJobDisplayValue(b); }
      else if (k === "customer") { av = a.customerName; bv = b.customerName; }
      else if (k === "source") { av = leadSourceForJob(a); bv = leadSourceForJob(b); }
      else if (k === "channel") { av = resolveChannelName(leadSourceForJob(a)); bv = resolveChannelName(leadSourceForJob(b)); }
      else if (k === "id") { av = a.id; bv = b.id; }
      else if (k === "score") { av = deriveBookingScore(a); bv = deriveBookingScore(b); }
      else if (k === "apptDate") { av = apptDateForJob(a); bv = apptDateForJob(b); }
      if (typeof av === "string") return jobSort.dir === "asc" ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return jobSort.dir === "asc" ? av - (bv as number) : (bv as number) - av;
    });
    return arr;
  }, [filtered, jobSort]);

  const pagination = usePagination(sorted, 15);
  const hasActiveFilters = statusFilter !== "all" || qualityFilter !== "all" || channelFilter !== "all";

  const totalRevenue = jobsWithMarketing.reduce((s, j) => s + getJobDisplayValue(j), 0);
  const totalLeadCost = jobsWithMarketing.reduce((s, j) => s + leadCostForJob(j), 0);
  const attributedRevenue = sourceData.reduce((s, r) => s + Number(r.revenue || 0), 0);
  const attributedLeadSpend = sourceData.reduce((s, r) => s + Number(r.leadCost || 0), 0);
  const attributedNetJobs = sourceData.reduce((s, r) => s + Number(r.netJobs || 0), 0);

  // ── CMO / Call-Center-CEO grade aggregate KPIs ──
  const execKpis = useMemo(() => {
    const closedJobs = jobsWithMarketing.filter((j) => j.marketing!.saleOutcome === "Closed");
    const closedRevenue = closedJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const totalAppts = jobsWithMarketing.length;
    const closedCount = closedJobs.length;
    const noShows = jobsWithMarketing.filter((j) => {
      const o = (j.marketing!.saleOutcome || "").toLowerCase();
      return o.includes("no show") || o.includes("no-show");
    }).length;
    const netJobs = jobsWithMarketing.filter(
      (j) => j.rescissionStatus === "Cleared for Production" || j.currentStage === "Completed",
    ).length;

    const revenueBase = attributedRevenue > 0 ? attributedRevenue : closedRevenue;
    const spendBase = attributedLeadSpend > 0 ? attributedLeadSpend : totalLeadCost;
    const netJobsBase = attributedNetJobs > 0 ? attributedNetJobs : jobsWithMarketing.filter(
      (j) => j.rescissionStatus === "Cleared for Production" || j.currentStage === "Completed",
    ).length;

    const roas = spendBase > 0 ? revenueBase / spendBase : 0;
    const closeRate = totalAppts > 0 ? (closedCount / Math.max(1, totalAppts - noShows)) * 100 : 0;
    const showRate = totalAppts > 0 ? ((totalAppts - noShows) / totalAppts) * 100 : 0;
    const cpaNet = netJobsBase > 0 ? spendBase / netJobsBase : 0;
    const costPerAppt = totalAppts > 0 ? spendBase / totalAppts : 0;
    const avgDealSize = netJobsBase > 0 ? revenueBase / netJobsBase : 0;
    const revPerAppt = totalAppts > 0 ? revenueBase / totalAppts : 0;

    // Top lead source by revenue
    const topLeadSource = [...sourceData]
      .sort((a, b) => b.revenue - a.revenue)[0];

    // Avg call-center speed-to-lead across reps (deterministic, see repData)
    const avgSpeedSec = repData.length > 0
      ? Math.round(repData.reduce((s, r) => s + r.speedSec, 0) / repData.length)
      : 0;

    return {
      roas, closeRate, showRate, cpaNet, costPerAppt, avgDealSize, revPerAppt,
      closedRevenue: revenueBase, closedCount, noShows, netJobs: netJobsBase, avgSpeedSec,
      topLeadSource: topLeadSource ? { name: topLeadSource.source, revenue: topLeadSource.revenue } : null,
    };
  }, [jobsWithMarketing, totalLeadCost, repData, sourceData, attributedRevenue, attributedLeadSpend, attributedNetJobs]);

  const fmtSpeed = (sec: number) => sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;

  return (
    <div className={embedded ? "space-y-5" : "space-y-5 max-w-7xl"}>
      {!embedded && (
        <>
          {/* Header — standalone route only */}
          <div>
            <BackToLeadPerformance />
            <h1 className="text-lg font-bold tracking-tight flex items-center gap-2 mt-1.5">
              <BarChart3 className="h-5 w-5 text-primary" /> Marketing Attribution
            </h1>
            <p className="text-xs text-muted-foreground">
              Source-level attribution, campaign reporting, and job-level drill-down
            </p>
            <div className="flex items-center gap-4 mt-1.5 text-[11px]">
              <Link to="/reports" className="text-muted-foreground hover:underline flex items-center gap-1">
                Reports <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>

          {/* KPIs — standalone route only */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Jobs Attributed", value: jobsForAttribution.length, icon: Target, color: "text-primary" },
              { label: "Lead Sources", value: sourceData.length, icon: TrendingUp, color: "text-info" },
              { label: "Attributed Revenue", value: `$${totalRevenue.toLocaleString()}`, icon: DollarSign, color: "text-success" },
              { label: "Total Lead Spend", value: `$${totalLeadCost.toLocaleString()}`, icon: DollarSign, color: "text-muted-foreground" },
            ].map((s) => (
              <Card key={s.label} className="border shadow-none">
                <CardContent className="p-3 text-center">
                  <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {embedded && showAttribution && (
        /* MARKETING / CMO lens — Attribution tab only */
        <dl className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-3 pb-1">
          <EmbedStat label="Attributed revenue" value={`$${totalRevenue.toLocaleString()}`} emphasis />
          <EmbedStat
            label="ROAS"
            value={execKpis.roas > 0 ? `${execKpis.roas.toFixed(1)}x` : "—"}
            tone={execKpis.roas >= 5 ? "good" : execKpis.roas < 2 ? "bad" : "neutral"}
            emphasis
          />
          <EmbedStat label="Lead spend" value={`$${totalLeadCost.toLocaleString()}`} />
          <EmbedStat
            label="CPA (net)"
            value={execKpis.cpaNet > 0 ? `$${execKpis.cpaNet.toFixed(0)}` : "—"}
            tone={execKpis.cpaNet > 500 ? "bad" : execKpis.cpaNet > 350 ? "warn" : "good"}
          />
          <EmbedStat label="Avg deal size" value={execKpis.avgDealSize > 0 ? `$${Math.round(execKpis.avgDealSize).toLocaleString()}` : "—"} />
          <EmbedStat label="Jobs attributed" value={String(jobsForAttribution.length)} sub={`${execKpis.netJobs} net`} />
          <EmbedStat
            label="Top lead source"
            value={execKpis.topLeadSource ? execKpis.topLeadSource.name : "—"}
            sub={execKpis.topLeadSource ? `$${execKpis.topLeadSource.revenue.toLocaleString()}` : undefined}
          />
        </dl>
      )}

      {embedded && showCallCenter && (
        /* CALL CENTER lens — Call Center tab only */
        <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 pb-1">
          <EmbedStat
            label="Avg speed-to-lead"
            value={execKpis.avgSpeedSec > 0 ? fmtSpeed(execKpis.avgSpeedSec) : "—"}
            tone={execKpis.avgSpeedSec <= 120 ? "good" : execKpis.avgSpeedSec <= 300 ? "warn" : "bad"}
            emphasis
          />
          <EmbedStat
            label="Show rate"
            value={`${execKpis.showRate.toFixed(0)}%`}
            tone={execKpis.showRate >= 80 ? "good" : execKpis.showRate < 65 ? "bad" : "warn"}
          />
          <EmbedStat
            label="Close rate"
            value={`${execKpis.closeRate.toFixed(0)}%`}
            tone={execKpis.closeRate >= 30 ? "good" : execKpis.closeRate < 15 ? "bad" : "neutral"}
            emphasis
          />
          <EmbedStat label="Cost / appt" value={execKpis.costPerAppt > 0 ? `$${execKpis.costPerAppt.toFixed(0)}` : "—"} />
          <EmbedStat label="Revenue / appt" value={execKpis.revPerAppt > 0 ? `$${Math.round(execKpis.revPerAppt).toLocaleString()}` : "—"} />
          <EmbedStat
            label="No-shows"
            value={`${execKpis.noShows} of ${jobsWithMarketing.length}`}
            tone={execKpis.noShows > execKpis.closedCount ? "bad" : "neutral"}
          />
        </dl>
      )}

      {embedded && showCampaigns && (
        /* CAMPAIGNS lens — Campaigns tab only */
        <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 pb-1">
          <EmbedStat label="Active campaigns" value={String(campaignData.length)} emphasis />
          <EmbedStat label="Campaign spend" value={`$${totalLeadCost.toLocaleString()}`} />
          <EmbedStat label="Attributed revenue" value={`$${totalRevenue.toLocaleString()}`} emphasis />
          <EmbedStat
            label="Blended ROAS"
            value={execKpis.roas > 0 ? `${execKpis.roas.toFixed(1)}x` : "—"}
            tone={execKpis.roas >= 5 ? "good" : execKpis.roas < 2 ? "bad" : "neutral"}
          />
          <EmbedStat
            label="Blended CPA"
            value={execKpis.cpaNet > 0 ? `$${execKpis.cpaNet.toFixed(0)}` : "—"}
            tone={execKpis.cpaNet > 500 ? "bad" : execKpis.cpaNet > 350 ? "warn" : "good"}
          />
          <EmbedStat label="Net jobs" value={String(execKpis.netJobs)} sub={`${execKpis.closedCount} closed`} />
        </dl>
      )}

      {/* ═══ ATTRIBUTION BY LEAD SOURCE ═══ */}
      {showAttribution && (
        <section>
          <SectionLabel icon={TrendingUp} label="Attribution by Lead Source" />
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border">
                  <SortHeader label="Source" sortKey="source" current={sourceSort} onSort={setSourceSort} align="left" />
                  <SortHeader label="Leads" sortKey="leads" current={sourceSort} onSort={setSourceSort} />
                  <SortHeader label="Jobs" sortKey="jobs" current={sourceSort} onSort={setSourceSort} />
                  <SortHeader label="Revenue" sortKey="revenue" current={sourceSort} onSort={setSourceSort} align="right" />
                  <SortHeader label="CPA (Net)" sortKey="cpaNet" current={sourceSort} onSort={setSourceSort} align="right" />
                  <SortHeader label="Net Conv %" sortKey="netConversion" current={sourceSort} onSort={setSourceSort} />
                  <SortHeader label="Quality %" sortKey="leadQuality" current={sourceSort} onSort={setSourceSort} />
                  <SortHeader label="Issues" sortKey="issues" current={sourceSort} onSort={setSourceSort} />
                </tr>
              </thead>
              <tbody>
                {sourceData.map((s) => (
                  <tr
                    key={s.source}
                    onClick={() => setSelectedSource(selectedSource === s.source ? null : s.source)}
                    className={`border-b border-border last:border-0 cursor-pointer transition-colors ${selectedSource === s.source ? "bg-muted/30" : "hover:bg-muted/20"}`}
                  >
                    <td className="py-2.5 px-2 font-medium text-foreground">{s.source}</td>
                    <td className="py-2.5 px-2 text-center tabular-nums">{s.leads}</td>
                    <td className="py-2.5 px-2 text-center tabular-nums">{s.jobs}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums font-semibold">${s.revenue.toLocaleString()}</td>
                    <td className={`py-2.5 px-2 text-right tabular-nums ${s.cpaNet <= 0 ? "text-muted-foreground/50" : s.cpaNet > 500 ? "text-destructive font-semibold" : s.cpaNet > 350 ? "text-warning" : "text-foreground"}`}>
                      {s.cpaNet > 0 ? `$${s.cpaNet.toFixed(0)}` : "—"}
                    </td>
                    <td className="py-2.5 px-2 text-center tabular-nums">{s.netConversion.toFixed(0)}%</td>
                    <td className={`py-2.5 px-2 text-center tabular-nums ${s.leadQuality < 60 ? "text-destructive font-semibold" : s.leadQuality < 80 ? "text-warning" : "text-foreground"}`}>
                      {s.leadQuality.toFixed(0)}%
                    </td>
                    <td className="py-2.5 px-2 text-center tabular-nums">
                      {s.issues > 0 ? <span className="text-destructive font-semibold">{s.issues}</span> : <span className="text-muted-foreground/50">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ═══ CALL CENTER / SETTER PERFORMANCE ═══ */}
      {showCallCenter && (
        <section>
          <SectionLabel icon={Phone} label="Call Center / Setter Performance" sub="Speed-to-lead, show rate, and conversion economics by rep — the call-center P&L view." />
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border">
                  <SortHeader label="Rep" sortKey="rep" current={repSort} onSort={setRepSort} align="left" />
                  <SortHeader label="Appts" sortKey="appointments" current={repSort} onSort={setRepSort} />
                  <SortHeader label="Speed-to-Lead" sortKey="speedSec" current={repSort} onSort={setRepSort} />
                  <SortHeader label="Show %" sortKey="showRate" current={repSort} onSort={setRepSort} />
                  <SortHeader label="Closed" sortKey="closed" current={repSort} onSort={setRepSort} />
                  <SortHeader label="Close %" sortKey="closeRate" current={repSort} onSort={setRepSort} />
                  <SortHeader label="High-Fit Conv %" sortKey="highFitConversion" current={repSort} onSort={setRepSort} />
                  <SortHeader label="Cost / Appt" sortKey="costPerAppt" current={repSort} onSort={setRepSort} align="right" />
                  <SortHeader label="Rev / Appt" sortKey="revenuePerAppt" current={repSort} onSort={setRepSort} align="right" />
                  <SortHeader label="Revenue" sortKey="revenue" current={repSort} onSort={setRepSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {repData.map((r) => (
                  <tr key={r.rep} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="py-2.5 px-2 font-medium text-foreground">{r.rep}</td>
                    <td className="py-2.5 px-2 text-center tabular-nums">{r.appointments}</td>
                    <td className={`py-2.5 px-2 text-center tabular-nums ${r.speedSec > 300 ? "text-destructive font-semibold" : "text-foreground"}`}>
                      {fmtSpeed(r.speedSec)}
                    </td>
                    <td className={`py-2.5 px-2 text-center tabular-nums ${r.showRate < 65 ? "text-destructive font-semibold" : "text-foreground"}`}>
                      {r.showRate.toFixed(0)}%
                    </td>
                    <td className="py-2.5 px-2 text-center tabular-nums font-semibold">{r.closed}</td>
                    <td className={`py-2.5 px-2 text-center tabular-nums ${r.closeRate < 15 ? "text-destructive font-semibold" : "text-foreground"}`}>
                      {r.closeRate.toFixed(0)}%
                    </td>
                    <td className="py-2.5 px-2 text-center tabular-nums">{r.highFitConversion.toFixed(0)}%</td>
                    <td className={`py-2.5 px-2 text-right tabular-nums ${r.costPerAppt <= 0 ? "text-muted-foreground/50" : "text-foreground"}`}>{r.costPerAppt > 0 ? `$${r.costPerAppt.toFixed(0)}` : "—"}</td>
                    <td className={`py-2.5 px-2 text-right tabular-nums ${r.revenuePerAppt <= 0 ? "text-muted-foreground/50" : "text-foreground"}`}>{r.revenuePerAppt > 0 ? `$${Math.round(r.revenuePerAppt).toLocaleString()}` : "—"}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums font-semibold">${r.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ═══ CAMPAIGNS — Decision-Driven Layout ═══ */}
      {showCampaigns && (
        <CampaignSection campaignData={campaignData} jobsWithMarketing={jobsWithMarketing} />
      )}

      {/* ═══ JOB-LEVEL ATTRIBUTION (Attribution tab only) ═══ */}
      {showAttribution && (
      <section>
        <SectionLabel icon={Target} label="Job-Level Attribution" sub="Ground-truth drill-down — every job linked to its source, channel, and revenue outcome." />

        <div className="flex flex-wrap items-center gap-3 mt-3 mb-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search job, customer, source, channel..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
          </div>
          <BranchRegionFilter jobs={jobs} value={brFilter} onChange={setBrFilter} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[130px] text-xs"><Filter className="h-3 w-3 mr-1 text-muted-foreground" /><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
              {uniqueStatuses.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={qualityFilter} onValueChange={setQualityFilter}>
            <SelectTrigger className="h-8 w-[130px] text-xs"><Filter className="h-3 w-3 mr-1 text-muted-foreground" /><SelectValue placeholder="Quality" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Quality</SelectItem>
              {uniqueQualities.map(q => <SelectItem key={q} value={q} className="text-xs">{q}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="h-8 w-[140px] text-xs"><Filter className="h-3 w-3 mr-1 text-muted-foreground" /><SelectValue placeholder="Channel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Channels</SelectItem>
              {uniqueChannels.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {(selectedSource || hasActiveFilters) && (
            <button onClick={() => { setSelectedSource(null); setStatusFilter("all"); setQualityFilter("all"); setChannelFilter("all"); }} className="text-xs text-primary hover:underline flex items-center gap-1">
              <X className="h-3 w-3" /> Clear filters
            </button>
          )}
        </div>

        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border">
                <SortHeader label="Job ID" sortKey="id" current={jobSort} onSort={setJobSort} align="left" px="px-3" />
                <SortHeader label="Customer" sortKey="customer" current={jobSort} onSort={setJobSort} align="left" px="px-3" />
                <SortHeader label="Lead Source" sortKey="source" current={jobSort} onSort={setJobSort} align="left" px="px-3" />
                <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Campaign</th>
                <SortHeader label="Channel" sortKey="channel" current={jobSort} onSort={setJobSort} align="left" px="px-3" />
                <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Rep</th>
                <SortHeader label="Appt Date" sortKey="apptDate" current={jobSort} onSort={setJobSort} align="left" px="px-3" />
                <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Status</th>
                <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Quality</th>
                <SortHeader label="Score" sortKey="score" current={jobSort} onSort={setJobSort} px="px-3" />
                <SortHeader label="Value" sortKey="value" current={jobSort} onSort={setJobSort} align="right" px="px-3" />
              </tr>
            </thead>
            <tbody>
              {pagination.paged.map((j) => {
                const outcome = deriveWonOutcome(j);
                const source = leadSourceForJob(j);
                const channel = resolveChannelName(source);
                const quality = deriveLeadQuality(j, jobIssueCount.get(j.id) || 0);
                const score = deriveBookingScore(j);
                return (
                  <tr key={j.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="py-2 px-3">
                      <Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{j.jobNumber || j.id}</Link>
                    </td>
                    <td className="py-2 px-3 font-medium">
                      <Link to={`/customers/${encodeURIComponent(j.customerName)}`} className="hover:text-primary transition-colors">{j.customerName}</Link>
                    </td>
                    <td className="py-2 px-3 text-foreground">{source || "Unmapped"}</td>
                    <td className="py-2 px-3 text-muted-foreground truncate max-w-[120px]">{j.marketing?.campaignName || "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{channel}</td>
                    <td className="py-2 px-3 text-foreground">{repForJob(j)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{formatApptDisplay(apptDateForJob(j))}</td>
                    <td className="py-2 px-3 text-center">
                      <StatusBadge outcome={outcome} stage={j.currentStage} />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <QualityTag quality={quality} />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`text-[11px] font-mono tabular-nums ${score < 70 ? "text-warning font-semibold" : "text-muted-foreground"}`}>
                        {score}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold">${getJobDisplayValue(j).toLocaleString()}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">No jobs match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 15 && (
          <div className="pt-3">
            <PaginationBar page={pagination.page} totalPages={pagination.totalPages} pageStart={pagination.pageStart} pageEnd={pagination.pageEnd} totalItems={pagination.totalItems} hasNext={pagination.hasNext} hasPrev={pagination.hasPrev} next={pagination.next} prev={pagination.prev} />
          </div>
        )}
      </section>
      )}
    </div>
  );
}

/* ── Section label (cockpit style) ── */
function SectionLabel({ icon: Icon, label, sub }: { icon: any; label: string; sub?: string }) {
  return (
    <div className="mb-2">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      {sub && <p className="text-[11px] text-muted-foreground/80 mt-1">{sub}</p>}
    </div>
  );
}

/* ── Sortable Header ── */
type SortState = { key: string; dir: "asc" | "desc" };
function SortHeader({ label, sortKey, current, onSort, align = "center", px = "px-2" }: {
  label: string; sortKey: string; current: SortState; onSort: (s: SortState) => void; align?: "left" | "center" | "right"; px?: string;
}) {
  const active = current.key === sortKey;
  const Icon = active ? (current.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`text-${align} py-2 ${px} font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors`}
      onClick={() => onSort({ key: sortKey, dir: active && current.dir === "desc" ? "asc" : "desc" })}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        <Icon className={`h-3 w-3 ${active ? "text-primary" : "opacity-40"}`} />
      </span>
    </th>
  );
}

/* ── Status Badge ── */
function StatusBadge({ outcome, stage }: { outcome: string; stage: string }) {
  const config: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
    Net: { variant: "default", label: "Net" },
    Pending: { variant: "secondary", label: "Pending" },
    Canceled: { variant: "destructive", label: "Canceled" },
    "Finance Declined": { variant: "destructive", label: "Fin. Declined" },
    "—": { variant: "outline", label: stage },
  };
  const c = config[outcome] || config["—"];
  return <Badge variant={c.variant} className="text-[9px] px-1.5 py-0 font-medium">{c.label}</Badge>;
}

/* ── Quality Tag ── */
function QualityTag({ quality }: { quality: "Valid" | "Out-of-Area" | "Low Quality" }) {
  const styles: Record<string, string> = {
    Valid: "bg-success/10 text-success",
    "Out-of-Area": "bg-warning/10 text-warning",
    "Low Quality": "bg-destructive/10 text-destructive",
  };
  return (
    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${styles[quality]}`}>
      {quality}
    </span>
  );
}

/* ── Campaign Status Badge ── */
function CampaignStatusBadge({ status }: { status: "Healthy" | "Needs Attention" | "Underperforming" }) {
  const styles: Record<string, string> = {
    Healthy: "bg-success/10 text-success border-success/20",
    "Needs Attention": "bg-warning/10 text-warning border-warning/20",
    Underperforming: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return <Badge variant="outline" className={`text-[9px] px-1.5 py-0 font-medium ${styles[status]}`}>{status}</Badge>;
}

/* ── Campaign Action Badge ── */
function CampaignActionBadge({ action }: { action: "Scale" | "Monitor" | "Review" | "Pause" }) {
  const styles: Record<string, string> = {
    Scale: "bg-primary/10 text-primary border-primary/20",
    Monitor: "bg-info/10 text-info border-info/20",
    Review: "bg-warning/10 text-warning border-warning/20",
    Pause: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return <Badge variant="outline" className={`text-[9px] px-1.5 py-0 font-medium ${styles[action]}`}>{action}</Badge>;
}

/* ── Campaign type for table ── */
type CampaignRow = {
  campaign: string; jobs: number; revenue: number; leadCost: number; netJobs: number;
  netConversion: number; cpa: number; sources: string; channel: string;
  status: "Healthy" | "Needs Attention" | "Underperforming";
  action: "Scale" | "Monitor" | "Review" | "Pause";
  descriptor: string;
};

/* ── Campaign Section (Decision-Driven) ── */
function CampaignSection({ campaignData, jobsWithMarketing }: { campaignData: CampaignRow[]; jobsWithMarketing: Job[] }) {
  const [campSort, setCampSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "revenue", dir: "desc" });

  const sortedCampaigns = useMemo(() => {
    const arr = [...campaignData];
    const k = campSort.key;
    arr.sort((a, b) => {
      const av = (a as any)[k] ?? 0;
      const bv = (b as any)[k] ?? 0;
      if (typeof av === "string") return campSort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return campSort.dir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [campaignData, campSort]);

  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const topRevenue = campaignData[0];
  const bestEfficiency = [...campaignData].filter(c => c.cpa > 0).sort((a, b) => a.cpa - b.cpa)[0];
  const needsAttention = campaignData.find(c => c.status !== "Healthy") || campaignData[campaignData.length - 1];

  const highlights = [
    topRevenue && { label: "Top Revenue", icon: Trophy, campaign: topRevenue.campaign, value: `$${topRevenue.revenue.toLocaleString()}`, sub: `${topRevenue.jobs} jobs · ${topRevenue.channel}`, desc: topRevenue.descriptor, color: "text-primary" },
    bestEfficiency && { label: "Best Efficiency", icon: Zap, campaign: bestEfficiency.campaign, value: bestEfficiency.cpa > 0 ? `$${bestEfficiency.cpa.toFixed(0)} CPA` : "—", sub: `${bestEfficiency.netConversion.toFixed(0)}% conv · ${bestEfficiency.channel}`, desc: bestEfficiency.descriptor, color: "text-success" },
    needsAttention && { label: "Needs Attention", icon: AlertTriangle, campaign: needsAttention.campaign, value: needsAttention.status, sub: `${needsAttention.channel}`, desc: needsAttention.descriptor, color: "text-warning" },
  ].filter(Boolean) as { label: string; icon: any; campaign: string; value: string; sub: string; desc: string; color: string }[];

  // Drill-down jobs for expanded campaign
  const drillJobs = expandedCampaign
    ? jobsWithMarketing.filter(j => j.marketing!.campaignName === expandedCampaign)
    : [];

  return (
    <section className="space-y-3">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Target className="h-3.5 w-3.5" /> Campaigns
      </p>

      {/* Highlight strip — hairline borders, restrained color */}
      {highlights.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-border rounded-md divide-y sm:divide-y-0 sm:divide-x divide-border">
          {highlights.map(h => (
            <div key={h.label} className="p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <h.icon className={`h-3.5 w-3.5 ${h.label === "Needs Attention" ? "text-warning" : "text-muted-foreground"}`} />
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{h.label}</span>
              </div>
              <p className="text-[12px] font-semibold truncate text-foreground">{h.campaign}</p>
              <p className={`text-[18px] font-bold tabular-nums leading-tight mt-0.5 ${h.label === "Needs Attention" ? "text-warning" : "text-foreground"}`}>{h.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{h.sub}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{h.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* All Campaigns Table — hairline lines, no card */}
      <div className="overflow-x-auto border-t border-border">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2.5 px-3 font-medium text-muted-foreground w-5"></th>
              <SortHeader label="Campaign" sortKey="campaign" current={campSort} onSort={setCampSort} align="left" px="px-3" />
              <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Channel / Source</th>
              <SortHeader label="Jobs" sortKey="jobs" current={campSort} onSort={setCampSort} px="px-3" />
              <SortHeader label="Revenue" sortKey="revenue" current={campSort} onSort={setCampSort} align="right" px="px-3" />
              <SortHeader label="Net Conv %" sortKey="netConversion" current={campSort} onSort={setCampSort} px="px-3" />
              <SortHeader label="CPA" sortKey="cpa" current={campSort} onSort={setCampSort} align="right" px="px-3" />
              <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Status</th>
              <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedCampaigns.map(c => {
              const isExpanded = expandedCampaign === c.campaign;
              return (
                <tr key={c.campaign} className="border-b border-border last:border-0">
                  <td colSpan={9} className="p-0">
                    <div
                      onClick={() => setExpandedCampaign(isExpanded ? null : c.campaign)}
                      className={`grid cursor-pointer transition-colors ${isExpanded ? "bg-muted/30" : "hover:bg-muted/20"}`}
                      style={{ gridTemplateColumns: "20px 1fr 1fr 60px 90px 80px 70px 100px 1fr" }}
                    >
                      <span className="py-2.5 px-3 flex items-center">
                        {isExpanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground/50" />}
                      </span>
                      <span className="py-2.5 px-3 font-medium truncate text-foreground">{c.campaign}</span>
                      <span className="py-2.5 px-3 text-muted-foreground truncate">{c.channel}</span>
                      <span className="py-2.5 px-3 text-center tabular-nums">{c.jobs}</span>
                      <span className="py-2.5 px-3 text-right tabular-nums font-semibold">${c.revenue.toLocaleString()}</span>
                      <span className="py-2.5 px-3 text-center tabular-nums">{c.netConversion.toFixed(0)}%</span>
                      <span className={`py-2.5 px-3 text-right tabular-nums ${c.cpa > 500 ? "text-destructive font-semibold" : c.cpa > 350 ? "text-warning" : "text-foreground"}`}>
                        {c.cpa > 0 ? `$${c.cpa.toFixed(0)}` : "—"}
                      </span>
                      <span className="py-2.5 px-3 text-center"><CampaignStatusBadge status={c.status} /></span>
                      <span className="py-2.5 px-3"><CampaignActionBadge action={c.action} /></span>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-border bg-muted/10 px-6 py-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Associated Jobs ({drillJobs.length})</p>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Job ID</th>
                              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Customer</th>
                              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Source</th>
                              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Stage</th>
                              <th className="text-right py-1.5 px-2 font-medium text-muted-foreground">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drillJobs.slice(0, 10).map(j => (
                              <tr key={j.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                                <td className="py-1.5 px-2">
                                  <Link to={`/jobs/${j.id}`} className="text-primary hover:underline font-medium">{j.jobNumber || j.id}</Link>
                                </td>
                                <td className="py-1.5 px-2">{j.customerName}</td>
                                <td className="py-1.5 px-2 text-muted-foreground">{j.marketing!.leadSource}</td>
                                <td className="py-1.5 px-2 text-muted-foreground">{j.currentStage}</td>
                                <td className="py-1.5 px-2 text-right tabular-nums font-semibold">${getJobDisplayValue(j).toLocaleString()}</td>
                              </tr>
                            ))}
                            {drillJobs.length > 10 && (
                              <tr><td colSpan={5} className="py-1.5 px-2 text-muted-foreground text-center">+{drillJobs.length - 10} more</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── Embedded stat (Channels/Sources-style consolidated metric) ── */
function EmbedStat({
  label, value, emphasis, tone = "neutral", sub,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: "good" | "warn" | "bad" | "neutral";
  sub?: string;
}) {
  const toneClass =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad"  ? "text-destructive" :
    "text-foreground";
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70">{label}</dt>
      <dd className={`mt-1 tabular-nums leading-none ${emphasis ? "text-[22px] font-bold" : "text-[18px] font-semibold"} ${toneClass}`}>
        {value}
      </dd>
      {/* Reserve baseline so metrics with/without a sub-line stay aligned */}
      <dd className="mt-1 text-[10px] text-muted-foreground tabular-nums min-h-[12px]">
        {sub ?? "\u00A0"}
      </dd>
    </div>
  );
}
